import type { CityConfig } from "../city-template/types";

export const fortMitchellConfig: CityConfig = {
  vitalRecordsIcon: "ri-file-text-line",
  city: "Fort Mitchell",
  state: "Kentucky",
  stateAbbr: "KY",
  slug: "fort-mitchell",
  route: "/fort-mitchell",
  title: "Emergency Passport Fort Mitchell, KY — Expedited & Fast Passport Service | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Fort Mitchell, KY — as fast as 24 hours. Fast passport renewal and child passport expediting for Kenton County and Northern Kentucky travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Fort Mitchell, KY",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Fort Mitchell and Kenton County travelers get their expedited passports as quickly as 24 hours. Serving Covington, Edgewood, Crestview Hills, and all of Northern Kentucky. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Fort%20Mitchell%20Kentucky%20Northern%20Kentucky%20residential%20community%20near%20Cincinnati%20with%20rolling%20hills%20and%20suburban%20neighborhoods%2C%20Ohio%20River%20valley%20in%20the%20distance%2C%20warm%20golden%20hour%20light%20over%20Kenton%20County%20Kentucky%2C%20clean%20panoramic%20cityscape%20with%20bridge%20views%2C%20lush%20green%20landscape%2C%20no%20visible%20text%20or%20people%2C%20wide%20panoramic%20view&width=1800&height=700&seq=fort-mitchell-hero-001&orientation=landscape",
  heroImageAlt: "Fort Mitchell Kentucky suburban community in Kenton County with Northern Kentucky hills and Ohio River valley views",
  geoPosition: "39.0556;-84.5549",
  geoPlacename: "Fort Mitchell, Kentucky",
  regionalAgency: {
    name: "Chicago Passport Agency",
    address: "77 W Jackson Blvd, Suite 1200, Chicago, IL 60604",
    distance: "Located in downtown Chicago — approximately 295 miles from Fort Mitchell via I-71",
  },
  nearbyCitiesZips: [
    { name: "Fort Mitchell", zips: "41017" },
    { name: "Covington", zips: "41011, 41014, 41015, 41016" },
    { name: "Edgewood", zips: "41017" },
    { name: "Crestview Hills", zips: "41017" },
    { name: "Newport", zips: "41071, 41072, 41073, 41074, 41075, 41076, 41099" },
    { name: "Florence", zips: "41022, 41042" },
    { name: "Erlanger", zips: "41018" },
    { name: "Independence", zips: "41051" },
    { name: "Fort Wright", zips: "41011" },
    { name: "Taylor Mill", zips: "41015" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Northern Kentucky departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Covington", route: "/covington" },
    { name: "Fast passport renewal in Newport", route: "/newport" },
    { name: "Emergency passport help in Florence", route: "/florence" },
    { name: "Urgent passport services in Erlanger", route: "/erlanger" },
    { name: "Same-day passport help in Fort Thomas", route: "/fort-thomas" },
    { name: "Passport courier services in Independence", route: "/independence" },
  ],
  fpcOfficeAddress: "500 W Jefferson St, Louisville, KY 40202",
  fpcOfficeNote: "Fast Passport Center serves Fort Mitchell remotely via secure courier. We do not have a walk-in office in Fort Mitchell — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Kenton County travelers.",
  fpcOfficeDistance: "Serving all of Kenton County and Northern Kentucky",
  serviceAreaIntro: "From Fort Mitchell's residential neighborhoods to the Crestview Hills town center, from Edgewood to the Covington border — Fast Passport Center serves the entire Fort Mitchell community and greater Northern Kentucky. We regularly help travelers from Covington, Newport, and Florence get their passports fast.",
  nearbyCitiesIntro: "We serve Fort Mitchell and all surrounding Northern Kentucky cities including Covington, Edgewood, Crestview Hills, Newport, Florence, Erlanger, Independence, Fort Wright, and Taylor Mill. If you are in Kenton County or anywhere in the Cincinnati-Northern Kentucky metro area, we have you covered.",
  howItWorksIntro: "Most Fort Mitchell travelers complete everything online and ship documents overnight. No need to drive to Chicago or wait in line at the regional agency.",
  mistakesIntro: "Fort Mitchell and Kenton County travelers often face long drives to the nearest regional passport agency. Here are the most common mistakes that cause delays.",
  testimonialsIntro: "Fort Mitchell and Northern Kentucky travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Fort Mitchell? Here are the most common ones we hear from Kenton County travelers.",
  statePageRoute: "/states/kentucky",
  statePageName: "Kentucky",
  dmvWebsite: "https://drive.ky.gov",
  dmvLabel: "Kentucky Transportation Cabinet — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns and budget fights",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Kenton County international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Rachel H.",
      location: "Fort Mitchell, KY",
      rating: 5,
      text: "My passport expired two weeks before our Ireland trip. Fast Passport Center got it renewed in 4 days and we didn't miss a single thing. Absolutely incredible service.",
      service: "Passport Renewal",
    },
    {
      name: "Michael T.",
      location: "Edgewood, KY",
      rating: 5,
      text: "Last-minute business trip to London. They had my emergency passport done in 36 hours. The agent kept me updated the whole time. Worth every penny.",
      service: "Emergency Passport",
    },
    {
      name: "Susan C.",
      location: "Crestview Hills, KY",
      rating: 5,
      text: "First passport for our daughter before our family trip to Mexico. They made the process so easy and we had the passport in 5 days. Highly recommend to all Northern Kentucky families.",
      service: "Child Passport",
    },
    {
      name: "James W.",
      location: "Fort Mitchell, KY",
      rating: 5,
      text: "Lost my passport the night before a Caribbean cruise departure. They worked around the clock and got me a replacement passport in time. Unbelievable turnaround.",
      service: "Lost Passport",
    },
  ],
  facilities: [
    {
      id: "fort-mitchell-1",
      name: "Covington Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1400 Scott Blvd",
      city: "Covington",
      state: "KY",
      zip: "41011",
      phone: "(859) 655-7900",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "1.5 miles from Fort Mitchell",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Closest major passport acceptance facility to Fort Mitchell. Appointments required. Call ahead to confirm times.",
      icon: "ri-mail-line",
    },
    {
      id: "fort-mitchell-2",
      name: "Kenton County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "303 Court St",
      city: "Covington",
      state: "KY",
      zip: "41011",
      phone: "(859) 392-1650",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "2 miles from Fort Mitchell",
      website: "https://www.kentoncounty.org/",
      websiteLabel: "Kenton County Clerk",
      notes: "County-level passport acceptance. Appointments recommended for faster service.",
      icon: "ri-government-line",
    },
    {
      id: "fort-mitchell-3",
      name: "Florence Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "8035 US-42",
      city: "Florence",
      state: "KY",
      zip: "41042",
      phone: "(859) 525-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "5 miles from Fort Mitchell",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Boone County residents. Full passport services with appointment.",
      icon: "ri-mail-line",
    },
    {
      id: "fort-mitchell-4",
      name: "Newport Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "200 York St",
      city: "Newport",
      state: "KY",
      zip: "41071",
      phone: "(859) 261-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "3 miles from Fort Mitchell",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Campbell County. Photo services available nearby.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 5 hour drive to Chicago", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Northern Kentucky photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Kenton County acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Fort Mitchell applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the Chicago regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Kenton County families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Fort Mitchell travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Fort Mitchell or Covington." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Fort Mitchell address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Fort Mitchell before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Fort Mitchell for travelers with urgent international flights. Our registered couriers hand-deliver documents directly to U.S. Department of State facilities, achieving 24–48 hour turnaround — without the 5 hour drive to Chicago." },
    { q: "How fast can I renew my passport in Fort Mitchell?", a: "Fast Passport Center offers fast passport renewal in Fort Mitchell in as little as 24–48 hours through our emergency courier service. For less urgent travel, our 3–5 day expedited option is the best value. Both options include full document review and a dedicated agent." },
    { q: "Where is the nearest passport acceptance facility to Fort Mitchell?", a: "The closest options are the Covington Post Office (41011), Kenton County Clerk (41011), Newport Post Office (41071), and Florence Post Office (41042). All require appointments. We help you find the most convenient location." },
    { q: "Can I expedite a child passport in Fort Mitchell?", a: "Yes. Fast Passport Center expedites child passports in Fort Mitchell for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs." },
    { q: "Do I need to drive to the Chicago Passport Agency?", a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities." },
    { q: "How much does expedited passport service cost in Fort Mitchell?", a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed. Contact us for a personalized quote." },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Covington", address: "514 Madison Ave, Covington, KY 41011", hours: "Mon–Sun: 8AM–10PM", website: "https://www.cvs.com/store-locator" },
    { name: "Walgreens — Florence", address: "7605 US-42, Florence, KY 41042", hours: "Mon–Sun: 7AM–11PM", website: "https://www.walgreens.com/storelocator" },
    { name: "Walmart — Florence", address: "7625 Mall Rd, Florence, KY 41042", hours: "Mon–Sun: 6AM–11PM", website: "https://www.walmart.com/store/finder" },
    { name: "FedEx Office — Covington", address: "1 W Rivercenter Blvd, Covington, KY 41011", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM", website: "https://www.fedex.com/en-us/location.html" },
  ],
  dmvLocations: [
    {
      name: "Circuit Court Clerk — Kenton County",
      address: "303 Court St, Covington, KY 41011",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Driver's license and REAL ID. Appointment recommended.",
      featured: true,
    },
    {
      name: "Circuit Court Clerk — Campbell County",
      address: "330 York St, Newport, KY 41071",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "REAL ID and driver's license renewals. Online booking available.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "CVG",
      name: "Cincinnati/Northern Kentucky International",
      distance: "8 miles from Fort Mitchell",
      address: "3087 Terminal Dr, Hebron, KY 41048",
      note: "The primary international airport for Northern Kentucky and the Cincinnati metro area. Major hub for international flights.",
      flightUrl: "https://www.cvgairport.com/",
      flightLabel: "CVG Airport",
      featured: true,
    },
    {
      code: "DAY",
      name: "Dayton International Airport",
      distance: "55 miles from Fort Mitchell",
      address: "3600 Terminal Blvd, Dayton, OH 45377",
      note: "Alternative airport for regional and some international flights.",
      flightUrl: "https://www.daytonairport.com/",
      flightLabel: "Dayton Airport",
      featured: false,
    },
  ],
  vitalRecordsLinks: [
    { icon: "ri-file-text-line", label: "Birth & Death Certificates", desc: "Kentucky Office of Vital Statistics in Frankfort — order certified copies online, by mail, or in person for passport applications and name changes.", url: "https://www.chfs.ky.gov/agencies/dph/dehp/vsb/Pages/default.aspx" },
    { icon: "ri-heart-line", label: "Marriage Records", desc: "Kenton County Clerk — certified marriage licenses for name change passport applications and legal documentation.", url: "https://www.kentoncountyky.gov/" },
    { icon: "ri-scales-line", label: "Divorce Decrees", desc: "Kenton Circuit Court Clerk — certified divorce decrees supporting passport name change or correction.", url: "https://kycourts.gov/Courts/Circuit-Court/Pages/Kenton.aspx" },
    { icon: "ri-global-line", label: "VitalChek — Nationwide Records", desc: "If your record was issued outside Kentucky, order certified copies online through VitalChek for delivery in 5–10 business days.", url: "https://www.vitalchek.com/" },
  ],
};